import { useState } from "react";
import {
  TextField,
  Typography,
  Box,
  Snackbar,
  Alert,
  Grid2,
  Button,
} from "@mui/material";
import LocationOnIcon from "@mui/icons-material/LocationOn";
import PhoneIcon from "@mui/icons-material/Phone";
import EmailIcon from "@mui/icons-material/Email";
import SendIcon from "@mui/icons-material/Send";
import { isMobile, isTablet } from "react-device-detect";
import { useTranslation } from "react-i18next";
import styles from "./ContactUs.module.scss";
import { PrimaryButton } from "../../library/common";
import { axiosInstance } from "../../services";
import { useInfo } from "../../context/InfoContext";
import localesKeys from "../../locales/localesKeys.json";
import MetaTag from "../../library/meta";

const formDataInit = {
  name: "",
  email: "",
  subject: "",
  message: "",
};

const ContactUs = () => {
  const { data: userInfo } = useInfo();
  const { t, i18n } = useTranslation();
  const { language } = i18n;

  const [formData, setFormData] = useState(formDataInit);
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: "",
    severity: "success",
  });

  const { contactUs: infoContactUs, bankDetails: infoBankDetails } =
    userInfo || {};
  const {
    bankName: infoBankName,
    accountName: infoAccountName,
    accountNumber: infoAccountNumber,
    ifscCode: infoIfscCode,
    branch: infoBranch,
    "UPI-ID": infoUpiId,
  } = infoBankDetails || {};
  const {
    email: infoEmail,
    phoneNo: infoPhoneNo,
    location: infoLocation,
  } = infoContactUs || {};

  const upiLink = `upi://pay?pa=${infoUpiId}&pn=GEO%20Jainism&cu=INR`;
  const qrUrl = `https://api.qrserver.com/v1/create-qr-code/?size=250x250&data=${encodeURIComponent(
    upiLink
  )}`;

  const isDeviceMobile = isMobile || isTablet;

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value,
    });
  };

  const handleSubmit = (event) => {
    event.preventDefault();
    const { name, email, subject, message } = formData;
    if (!name || !email || !subject || !message) {
      setSnackbar({
        open: true,
        message: t(t(localesKeys.CONTACTUS_VALIDATION_REQUIRED)),
        severity: "error",
      });
      return;
    }
    if (!/\S+@\S+\.\S+/.test(email)) {
      setSnackbar({
        open: true,
        message: t(t(localesKeys.CONTACTUS_VALIDATION_INVALID_EMAIL)),
        severity: "error",
      });
      return;
    }
    if (message.length < 10) {
      setSnackbar({
        open: true,
        message: t(t(localesKeys.CONTACTUS_VALIDATION_SHORT_MESSAGE)),
        severity: "error",
      });
      return;
    }
    if (subject.length < 5) {
      setSnackbar({
        open: true,
        message: t(t(localesKeys.CONTACTUS_VALIDATION_SHORT_SUBJECT)),
        severity: "error",
      });
      return;
    }
    if (name.length < 3) {
      setSnackbar({
        open: true,
        message: t(t(localesKeys.CONTACTUS_VALIDATION_SHORT_NAME)),
        severity: "error",
      });
      return;
    }
    if (message.length > 500) {
      setSnackbar({
        open: true,
        message: t(t(localesKeys.CONTACTUS_VALIDATION_LONG_MESSAGE)),
        severity: "error",
      });
      return;
    }
    if (subject.length > 100) {
      setSnackbar({
        open: true,
        message: t(t(localesKeys.CONTACTUS_VALIDATION_LONG_SUBJECT)),
        severity: "error",
      });
      return;
    }
    if (name.length > 50) {
      setSnackbar({
        open: true,
        message: t(t(localesKeys.CONTACTUS_VALIDATION_LONG_NAME)),
        severity: "error",
      });
      return;
    }
    if (email.length > 100) {
      setSnackbar({
        open: true,
        message: t(t(localesKeys.CONTACTUS_VALIDATION_LONG_EMAIL)),
        severity: "error",
      });
      return;
    }
    axiosInstance
      .post("/contact", {
        email,
        name,
        subject,
        phone: "",
        message,
      })
      .then((res) => {
        if (res?.code === 201) {
          setSnackbar({
            open: true,
            message: res?.message,
            severity: "success",
          });
          event.target.reset();
          setFormData(formDataInit);
        }
      })
      .catch((err) => {
        setSnackbar({
          open: true,
          message: err?.response?.data?.message
            ? err?.response?.data?.message
            : err?.message,
          severity: "error",
        });
      });
  };

  const handleCloseSnackbar = () => {
    setSnackbar({ ...snackbar, open: false });
  };

  return (
    <div className={styles.mainContainer}>
      <MetaTag url="/contact" title={t(localesKeys.NAVIGATION_CONTACT)} />
      <Grid2 size={{ lg: 12 }} className={styles.contactContainer}>
        <Box className={styles.headerSection}>
          <Typography variant="h1" className={styles.title}>
            {t(t(localesKeys.ContactUs_TITLE))}
          </Typography>
          <Typography variant="body1" className={styles.subtitle}>
            {t(t(localesKeys.CONTACTUS_DESCRIPTION))}
          </Typography>
        </Box>

        <Grid2
          container
          spacing={{ xs: 0, md: 4 }}
          className={styles.contentWrapper}
        >
          {/* Contact Form */}
          <Grid2 item size={{ xs: 12, sm: 12, md: 7, lg: 8 }}>
            <Box
              component="form"
              onSubmit={handleSubmit}
              className={styles.formSection}
            >
              <Grid2 container spacing={{ xs: 3, md: 6 }}>
                <Grid2 item size={{ xs: 12, lg: 6 }}>
                  <TextField
                    fullWidth
                    name="name"
                    value={formData.name}
                    onChange={handleChange}
                    variant="standard"
                    label={t(t(localesKeys.CONTACTUS_FORM_NAME))}
                    required
                    className={styles.textField}
                  />
                </Grid2>
                <Grid2 item size={{ xs: 12, lg: 6 }}>
                  <TextField
                    fullWidth
                    name="email"
                    value={formData.email}
                    onChange={handleChange}
                    variant="standard"
                    label={t(t(localesKeys.CONTACTUS_FORM_EMAIL))}
                    type="email"
                    required
                    className={styles.textField}
                  />
                </Grid2>
                <Grid2 item size={{ xs: 12 }}>
                  <TextField
                    fullWidth
                    name="subject"
                    value={formData.subject}
                    onChange={handleChange}
                    variant="standard"
                    label={t(t(localesKeys.CONTACTUS_FORM_SUBJECT))}
                    required
                    className={styles.textField}
                  />
                </Grid2>
                <Grid2 item size={{ xs: 12 }}>
                  <TextField
                    fullWidth
                    name="message"
                    value={formData.message}
                    onChange={handleChange}
                    multiline
                    rows={4}
                    variant="standard"
                    label={t(t(localesKeys.CONTACTUS_FORM_MESSAGE))}
                    required
                    className={styles.textField}
                  />
                </Grid2>
                <Grid2 item size={{ xs: 12 }}>
                  <PrimaryButton
                    className={styles.submitButton}
                    type="submit"
                    title={t(t(localesKeys.CONTACTUS_FORM_SEND))}
                    icon={<SendIcon />}
                  />
                </Grid2>
              </Grid2>
            </Box>
          </Grid2>

          {/* Contact Info */}
          <Grid2 item size={{ xs: 12, sm: 12, md: 5, lg: 4 }}>
            <Box className={styles.contactInfo}>
              <Typography variant="h4" className={styles.infoTitle}>
                {t(t(localesKeys.CONTACTUS_INFO_TITLE))}
              </Typography>

              <Box className={styles.infoItem}>
                <LocationOnIcon className={styles.infoIcon} />
                <Box className={styles.infoContent}>
                  <Typography variant="h6">
                    {t(t(localesKeys.CONTACTUS_INFO_LOCATION))}
                  </Typography>
                  <Typography>{infoLocation?.address?.[language]}</Typography>
                  <Typography>{infoLocation?.state?.[language]}</Typography>
                </Box>
              </Box>

              <Box className={styles.infoItem}>
                <PhoneIcon className={styles.infoIcon} />
                <Box className={styles.infoContent}>
                  <Typography variant="h6">
                    {t(t(localesKeys.CONTACTUS_INFO_PHONE))}
                  </Typography>
                  <Typography>{infoPhoneNo}</Typography>
                </Box>
              </Box>

              <Box className={styles.infoItem}>
                <EmailIcon className={styles.infoIcon} />
                <Box className={styles.infoContent}>
                  <Typography variant="h6">
                    {t(t(localesKeys.CONTACTUS_INFO_EMAIL))}
                  </Typography>
                  <Typography>{infoEmail}</Typography>
                </Box>
              </Box>
            </Box>
          </Grid2>
        </Grid2>

        {/* Bank Details */}
        <Box className={styles.bankDetailsContainer}>
          <Box className={styles.bankDetailsContent}>
            <Typography variant="h4" className={styles.bankDetailsTitle}>
              {t(t(localesKeys.CONTACTUS_BANK_TITLE))}
            </Typography>
            <Typography variant="body1">
              <strong>{t(t(localesKeys.CONTACTUS_BANK_BANKNAME))}:</strong>{" "}
              {infoBankName?.[language]}
            </Typography>
            <Typography variant="body1">
              <strong>{t(t(localesKeys.CONTACTUS_BANK_ACCOUNTNAME))}:</strong>{" "}
              {infoAccountName?.[language]}
            </Typography>
            <Typography variant="body1">
              <strong>{t(t(localesKeys.CONTACTUS_BANK_ACCOUNTNUMBER))}:</strong>{" "}
              {infoAccountNumber}
            </Typography>
            <Typography variant="body1">
              <strong>{t(t(localesKeys.CONTACTUS_BANK_IFSC))}:</strong>{" "}
              {infoIfscCode}
            </Typography>
            <Typography variant="body1">
              <strong>{t(t(localesKeys.CONTACTUS_BANK_BRANCH))}:</strong>{" "}
              {infoBranch?.[language]}
            </Typography>
            <Typography variant="body1">
              <strong>{t(t(localesKeys.CONTACTUS_BANK_UPI))}:</strong>{" "}
              {infoUpiId}
            </Typography>
          </Box>
          <Box className={styles.qrSection}>
            <Typography variant="body1" className={styles.scanTitle}>
              <span style={{ fontWeight: "bold" }}>
                {t(t(localesKeys.CONTACTUS_BANK_SCAN))}
              </span>
            </Typography>
            <img
              src={qrUrl}
              alt="UPI QR Code"
              className={styles.qrImage}
              loading="lazy"
            />
            {isDeviceMobile && (
              <Button
                variant="contained"
                className={styles.payButton}
                href={upiLink}
                role="link"
              >
                {t(t(localesKeys.CONTACTUS_DONATENOW))}
              </Button>
            )}
          </Box>
        </Box>

        <Snackbar
          open={snackbar.open}
          autoHideDuration={6000}
          onClose={handleCloseSnackbar}
          anchorOrigin={{ vertical: "top", horizontal: "right" }}
        >
          <Alert onClose={handleCloseSnackbar} severity={snackbar.severity}>
            {snackbar.message}
          </Alert>
        </Snackbar>
      </Grid2>
    </div>
  );
};

export default ContactUs;
